import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ContentTeaser() {
  const [latestVideo, setLatestVideo] = useState(null);
  const [latestArticle, setLatestArticle] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [activeTab, setActiveTab] = useState('article'); // Start with article first
  const [isAutoSliding, setIsAutoSliding] = useState(true);

  useEffect(() => {
    fetchLatestContent();
    
    // Show teaser after 3 seconds
    const showTimer = setTimeout(() => {
      setIsVisible(true);
    }, 3000);

    return () => clearTimeout(showTimer);
  }, []);

  // Auto-slide between article and video every 5 seconds
  useEffect(() => {
    if (!isAutoSliding || isMinimized) return;

    const slideInterval = setInterval(() => {
      setActiveTab(prev => prev === 'article' ? 'video' : 'article');
    }, 5000); // 5 seconds per slide

    return () => clearInterval(slideInterval);
  }, [isAutoSliding, isMinimized]);

  const fetchLatestContent = async () => {
    try {
      // Fetch latest video
      const videoResponse = await axios.get(`${API}/videos/latest/teaser`);
      setLatestVideo(videoResponse.data);

      // Fetch latest article
      const articleResponse = await axios.get(`${API}/articles/latest/teaser`);
      setLatestArticle(articleResponse.data);
    } catch (err) {
      console.error('Error fetching teaser content:', err);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    // Store in localStorage to not show again for 24 hours
    localStorage.setItem('teaser-closed', new Date().toISOString());
  };

  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  // Check if teaser was closed in last 24 hours
  useEffect(() => {
    const closedTime = localStorage.getItem('teaser-closed');
    if (closedTime) {
      const closedDate = new Date(closedTime);
      const now = new Date();
      const hoursDiff = (now - closedDate) / (1000 * 60 * 60);
      
      if (hoursDiff < 24) {
        setIsVisible(false);
      }
    }
  }, []);

  if (!isVisible || (!latestVideo && !latestArticle)) return null;

  // Minimized state
  if (isMinimized) {
    return (
      <div 
        className="fixed bottom-6 right-6 z-50 cursor-pointer"
        onClick={toggleMinimize}
      >
        <div className="bg-black border-2 border-[#00FF7F] rounded-full p-4 shadow-2xl hover:scale-110 transition-transform animate-pulse-glow">
          <div className="relative">
            <svg className="w-8 h-8 text-[#00FF7F]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-ping"></span>
          </div>
        </div>
      </div>
    );
  }

  // Get YouTube thumbnail - using hqdefault for better compatibility
  const getYouTubeThumbnail = (videoId) => {
    return `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 w-96 animate-slide-up">
      <div className="bg-black border-2 border-[#00FF7F] rounded-lg shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-black to-zinc-900 px-4 py-3 border-b border-[#00FF7F]/30 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 bg-[#00FF7F] rounded-full animate-pulse"></span>
            <h3 className="text-white font-bold text-sm">LATEST FROM JOHN GHATTI</h3>
          </div>
          <div className="flex gap-2">
            <button 
              onClick={toggleMinimize}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <button 
              onClick={handleClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Tabs with Auto-Slide Indicator */}
        <div className="flex bg-zinc-900 relative">
          <button
            onClick={() => {
              setActiveTab('article');
              setIsAutoSliding(false); // Pause auto-slide on manual click
            }}
            className={`flex-1 py-2 text-sm font-medium transition-colors ${
              activeTab === 'article'
                ? 'bg-black text-[#00FF7F] border-b-2 border-[#00FF7F]'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            📰 New Article
          </button>
          <button
            onClick={() => {
              setActiveTab('video');
              setIsAutoSliding(false); // Pause auto-slide on manual click
            }}
            className={`flex-1 py-2 text-sm font-medium transition-colors ${
              activeTab === 'video'
                ? 'bg-black text-[#00FF7F] border-b-2 border-[#00FF7F]'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            🎥 New Video
          </button>
          {isAutoSliding && (
            <span className="absolute top-1 right-1 text-xs text-[#00FF7F] animate-pulse">●</span>
          )}
        </div>

        {/* Content with Slide Animation */}
        <div className="bg-black p-4 relative overflow-hidden">
          <div className={`transition-all duration-500 ${activeTab === 'video' ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-full absolute inset-0 p-4'}`}>
          {latestVideo && (
            <a
              href={latestVideo.youtube_url}
              target="_blank"
              rel="noopener noreferrer"
              className="block group"
            >
              {/* Video Thumbnail */}
              <div className="relative mb-3 overflow-hidden rounded bg-zinc-900">
                <img
                  src={getYouTubeThumbnail(latestVideo.video_id)}
                  alt={latestVideo.title}
                  className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://via.placeholder.com/480x360/000000/00FF7F?text=Video+Preview';
                  }}
                  loading="eager"
                  crossOrigin="anonymous"
                />
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center">
                    <svg className="w-8 h-8 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                </div>
                {/* NEW Badge */}
                <div className="absolute top-2 left-2 bg-red-600 text-white text-xs font-bold px-2 py-1 rounded">
                  NEW
                </div>
              </div>

              {/* Video Info */}
              <h4 className="text-white font-bold text-sm mb-2 group-hover:text-[#00FF7F] transition-colors line-clamp-2">
                {latestVideo.title}
              </h4>
              <p className="text-gray-400 text-xs mb-3 line-clamp-2">
                {latestVideo.description}
              </p>
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>{new Date(latestVideo.created_at).toLocaleDateString()}</span>
                <span className="text-[#00FF7F] font-medium group-hover:underline">
                  Watch Now →
                </span>
              </div>
            </a>
          )}
          </div>

          <div className={`transition-all duration-500 ${activeTab === 'article' ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-full absolute inset-0 p-4'}`}>
          {latestArticle && (
            <a
              href={`/article/${latestArticle.id}`}
              className="block group"
            >
              {/* Article Image */}
              {latestArticle.featured_image && (
                <div className="relative mb-3 overflow-hidden rounded">
                  <img
                    src={latestArticle.featured_image}
                    alt={latestArticle.title}
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  {/* NEW Badge */}
                  <div className="absolute top-2 left-2 bg-[#00FF7F] text-black text-xs font-bold px-2 py-1 rounded">
                    NEW
                  </div>
                  {/* Category */}
                  <div className="absolute bottom-2 left-2 bg-black text-white text-xs font-bold px-2 py-1 rounded">
                    {latestArticle.category.toUpperCase()}
                  </div>
                </div>
              )}

              {/* Article Info */}
              <h4 className="text-white font-bold text-sm mb-2 group-hover:text-[#00FF7F] transition-colors line-clamp-2">
                {latestArticle.title}
              </h4>
              <p className="text-gray-400 text-xs mb-3 line-clamp-2">
                {latestArticle.excerpt}
              </p>
              <div className="flex items-center justify-between text-xs text-gray-500">
                <div className="flex items-center gap-2">
                  <span>{latestArticle.author}</span>
                  <span>•</span>
                  <span>{new Date(latestArticle.created_at).toLocaleDateString()}</span>
                </div>
                <span className="text-[#00FF7F] font-medium group-hover:underline">
                  Read More →
                </span>
              </div>
            </a>
          )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ContentTeaser;
